import { Navigate, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { homeFor } from '../lib/portal';

/**
 * Gates the Tenant Portal on holding a tenant login.
 *
 * The mirror of RequireAdmin, and a convenience for the same reason: the real
 * boundary is the server, which resolves roles from the database on every
 * request, and beneath that the row-level security added in migrations 027 and
 * 028. Neither would let a landlord's session read a portal endpoint as though
 * it were a tenant — `/api/v1/portal/*` reads the tenant id from the signed-in
 * user's own row, and a landlord's row has none, so the call 404s.
 *
 * What this does is stop somebody arriving at a shell whose every request will
 * fail. A landlord who opens /portal out of curiosity gets their own dashboard,
 * not an empty portal that looks broken.
 */
export function RequireTenant({ children }: { children: ReactNode }) {
  const { isAuthenticated, roles } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!roles.includes('tenant_user')) {
    return <Navigate to={homeFor(roles)} replace />;
  }
  return <>{children}</>;
}
